import React, {
	FC,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import {
	Button,
	Image,
	Input,
	Loader,
	MAX_PHONE_DIGITS,
	fetchCountryCodeFromPhone,
	validatePhoneNumber,
} from '@storybook';
import { useCookie, useNetwork, useNotification } from 'hooks';
import { IsOpenInIframeState, NewGmailState } from 'global-stores';

import { API_URL, MESSAGE, TEST_PHONE_NUMBERS, isMobileView, isValidSubdomainEmail, validateEmail } from 'constant';
import {
	AcountLiveStatus,
	CountryCode,
	LoginCredsState,
	LoginMaskPhoneCredsState,
	LoginStep,
	MobileLoginLinkResponseState,
	useLogin,
} from '../../store';
import { SocialMark } from 'components/social-mark/social-mark';
import { ChooseMethod } from '../choose-method';
import { Messages } from './constant';
import countries from 'json/country-codes.json';

import style from './signin-form.module.sass';
import './signin-form.scss';
import { SessionInfoChild } from 'views/sessions';
import { VerifyModal, useSignup } from 'views/authentication';
import { isFreeEmail } from 'utils';

type ISignInForm = {
	input_type: string;
	methods: any;
	locationInfo: any;
	ipAddress: any;
	send_otp_api: any;
	props: any;
};

const { origin } = window.location;
const { ancestorOrigins = [] } = window.location;

const hasAncestorOrigins = ancestorOrigins?.length ?? 0 > 0;
const isSameOrigin =
	hasAncestorOrigins && origin === ancestorOrigins[ancestorOrigins.length - 1];

const host = isSameOrigin
	? ancestorOrigins[ancestorOrigins.length - 1]
	: `${origin}`;

export const SignInForm: FC<ISignInForm> = ({
	methods,
	locationInfo,
	ipAddress,
	send_otp_api,
	props,
}) => {
	//globle states
	const setNewGmail = useSetRecoilState(NewGmailState);
	const [loginCreds, setLoginCreds] = useRecoilState(LoginCredsState);
	const setMaskPhoneCreds = useSetRecoilState(LoginMaskPhoneCredsState);
	const [country, setCountry] = useRecoilState(CountryCode);
	const [step, setStep] = useRecoilState(LoginStep);
	const setSendLinkResponse = useSetRecoilState(MobileLoginLinkResponseState);
	const isIframe = useRecoilValue(IsOpenInIframeState);
	const { deleteCookie } = useCookie();

	//local states
	const [emailCode, setEmailCode] = useState('');
	const [invalidPhoneMessage, setInvalidPhoneMessage] = useState('');
	const [isLargeErrorMsg, setIsLargeErrorMsg] = useState('');
	const [invalidEmailCodeMessage, setInvalidEmailCodeMessage] = useState('');
	const [emailBlurError, setEmailBlurError] = useState('');
	const [acountLiveStatus, setAcountLiveStatus] =
		useRecoilState(AcountLiveStatus);
	const [signInErrorType, setSignInErrorType] = useState({
		type: '',
		loading: false,
	});

	// Params
	const { error, countryCode, mobile } = useMemo(() => {
		const params = new URLSearchParams(window.location.search);
		const error = params.get('error');
		const mobile = params.get('phone');
		const countryCode = params.get('countryCode');
		return {
			error,
			countryCode,
			mobile,
		};
	}, []);

	// Refs
	const nextDivRef = useRef<HTMLDivElement>(null);

	const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);

	//hooks
	const {
		loginWithEmail,
		verifyEmailCode,
		loginWithSocial,
		isLoaded,
		isSocialLogin,
		isSocialDisable,
	} = useLogin();
	const { errorNotification, successNotification } = useNotification();
	const navigate = useNavigate();
	const { userVerfication } = useSignup();

	const { get: reinviteUser } = useNetwork();

	const { onSuccess, onFailed } = props;

	// Pradeep Chaurasia : regex for Mobile number with countryCode
	const hasCountryCode = (value: string) =>
		/^\+[1-9]{1}[0-9]{5,11}$/.test(value);

	const inputType = useMemo((): 'email' | 'phone' => {
		const isPureNumber = /^[0-9 ()-]+$/.test(loginCreds);
		if (
			(isPureNumber && loginCreds.length >= 3) ||
			hasCountryCode(loginCreds)
		) {
			return 'phone';
		}
		return 'email';
	}, [loginCreds]);

	const inputLabel = useMemo(() => {
		if (validateEmail(loginCreds)) {
			return 'Business Email Address';
		}
		return 'Business Email / Phone Number';
	}, [loginCreds]);

	const clearEmailVerificationLink = useCallback(() => {
		setSignInErrorType({
			loading: false,
			type: '',
		});
	}, []);

	const handleEmailBlur = useCallback(() => {
		if (inputType === 'email' && loginCreds.trim()) {
			if (!isValidSubdomainEmail(loginCreds.trim())) {
				setEmailBlurError('Please enter a valid email address');
			}else if(loginCreds.trim().length > 0 && isFreeEmail(loginCreds.trim())){
				setEmailBlurError('Please enter a valid company email address');
			}else {
				setEmailBlurError('');
			}
		}
	}, [inputType, loginCreds]);

	useEffect(() => {
		if (signInErrorType.type) {
			clearEmailVerificationLink();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loginCreds]);

	const sendVerificationCode = async (event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
		if (signInErrorType.type === 'mobile' || signInErrorType.type === 'email') {
			// call the api to send verification to mobile
			const { type } = signInErrorType;
			const verificationType =
				signInErrorType.type === 'mobile' ? 'sendSMS' : 'sendEmail';
			try {
				const response = await reinviteUser(
					`${API_URL.USER_ROLES}?email=${encodeURIComponent(
						loginCreds
					)}&${verificationType}=true`
				);
				if (response) {
					const {
						data: { success },
					} = response;
					if (success) {
						successNotification(
							`A verification link has been sent to your ${
								type === 'mobile' ? 'mobile' : 'email'
							}`
						);
						setSignInErrorType({
							type: '',
							loading: false,
						});
					} else {
						errorNotification(Messages.UnExpectedError);
						setSignInErrorType({
							...signInErrorType,
							loading: false,
						});
					}
				}
			} catch (error) {
				errorNotification(Messages.UnExpectedError);
			}
		}
		return;
	};

	const renderVerificationSendLink = () => {
		const linkDisabled = signInErrorType.loading;
		if (signInErrorType.type === 'mobile') {
			return (
				<div style={{ margin: '10px 0 0 0' }}>
					<a
						style={{
							textDecoration: 'underline',
							cursor: 'pointer',
							fontSize: '14px',
							pointerEvents: linkDisabled ? 'none' : 'auto',
						}}
						onClick={sendVerificationCode}
					>
						Resend a verification code to mobile
					</a>
				</div>
			);
		} else if (signInErrorType.type === 'email') {
			return (
				<div style={{ margin: '10px 0 0 0' }}>
					<a
						style={{
							textDecoration: 'underline',
							cursor: 'pointer',
							fontSize: '14px',
							pointerEvents: linkDisabled ? 'none' : 'auto',
						}}
						onClick={sendVerificationCode}
					>
						Resend a verification Link to Email
					</a>
				</div>
			);
		} else {
			return <></>;
		}
	};

	const extractCountryCodeAndNumber = (inputNumber: string) => {
		if (inputType === 'phone') {
			const _country = countries.find(country =>
				inputNumber.startsWith(country.label)
			);

			if (_country) {
				const _countryCode = _country.label;
				const _mobile = inputNumber.slice(_country.label.length);
				return {
					countryCode: _countryCode,
					mobile: _mobile,
				};
			}
		}
		return null;
	};

	/* CHOOSE METHOD IS BACK IN THE FLOW (2026-09-16).
	 *
	 * It was removed on 2026-09-15 because it and the approval-link chooser
	 * "asked the same question twice" — true of its second row, which only
	 * advanced to the chooser with nothing sent. But its FIRST row is Device
	 * Biometric, and removing the screen took the passkey path out of login
	 * altogether.
	 *
	 * That mattered more than the duplicate question: on an environment where
	 * SMS is suppressed (`SMS_SUPPRESS=true` on dev2) and approval email does
	 * not land, the passkey was the only method that could still sign anyone
	 * in. Losing it meant no way into dev2 at all — which is what "Google SSO
	 * is broken" turned out to be: SSO completes, hands off to this step, and
	 * the only workable option had been deleted.
	 *
	 * Going to VERIFY rather than straight to MOBILE_CODE restores the choice.
	 * The chooser still sends nothing until a channel is picked, so the second
	 * screen remains honest about what has been sent.
	 */
	const goToChooser = useCallback(() => {
		setSendLinkResponse({ verificationId: '' });
		setStep('VERIFY');
	}, [setSendLinkResponse, setStep]);

	const handleSubmitCreds = useCallback(async () => {
		const isValidEmail = isValidSubdomainEmail(loginCreds);
		const validMobileNumber = extractCountryCodeAndNumber(loginCreds);
		const countryObj = countries.find(item => item.label === country);
		const { isValid: isPhoneNumberValid } = validatePhoneNumber(
			loginCreds,
			country,
			countryObj?.value
		);
	
		if (inputType === 'email' && !validMobileNumber) {
			if (isValidEmail) {
				const payload = {
					type: 'email',
					email: loginCreds.toLowerCase().trim(),
				};
				const response = await loginWithEmail(payload);
				if (response) {
					const {
						message,
						success,
						token,
						liveAccount,
						countryCode,
						phone,
						errorCode,
						errorData,
					} = response;
					if (errorCode === 400) {
						if (errorData.message === Messages.PhoneError) {
							setSignInErrorType({
								...signInErrorType,
								type: 'mobile',
							});
							errorNotification(`${Messages.PhoneErrorMessage}`);
						} else if (errorData.message === Messages.AccountLocked) {
							errorNotification(`${Messages.AccountLockedErrorMessage}`);
						} else if (errorData.message === Messages.EmailError) {
							setSignInErrorType({
								...signInErrorType,
								type: 'email',
							});
							errorNotification(`${Messages.EmailErrorMesssage}`);
						} else {
							setNewGmail(loginCreds);
							navigate('/signup');
						}
						return;
					}
					if (errorCode) {
						onFailed({
							...(errorData.message ?? MESSAGE.ERROR),
							type: 'email',
						});
						setIsLargeErrorMsg(errorData.message);
						return;
					}
					if (liveAccount) {
						setAcountLiveStatus(liveAccount);
						setCountry(`${countryCode.trim()}`);
						setMaskPhoneCreds(phone);
					} else {
						setAcountLiveStatus(false);
					}
					if (success) {
						if (token) {
							onSuccess({ ...response, type: 'email' });
							return;
						}
						goToChooser();
						return;
					}
					setIsLargeErrorMsg(message);
					return;
				}
				onFailed({ ...(response ?? MESSAGE.ERROR), type: 'email' });
				setIsLargeErrorMsg(Messages.loginfaildeMessage);
				return;
			}
			// Arun kumar : Giving gap to next btn wrapper
			if (nextDivRef.current) {
				nextDivRef.current.style.marginTop = '0px';
			}
			setIsLargeErrorMsg(Messages.InvalidEmail);
			return;
		} else if (inputType === 'phone') {

			// Handle phone number case
			// Check if it's a test phone number and bypass validation (only on stage environment)
			const isTestPhoneNumber =  TEST_PHONE_NUMBERS.includes(loginCreds.toLowerCase().trim());	
			if (!isPhoneNumberValid && !isTestPhoneNumber) {
				// Set error message for invalid phone number
				if (countryObj?.value && countryObj?.value.length > 0) {
					setInvalidPhoneMessage(`Mobile number is invalid for ${countryObj?.value}`);
				} else {
					setInvalidPhoneMessage(`Invalid mobile number`);
				}
				return;
			}
			const payload = {
				type: 'phoneApproveStatus',
				phone: validMobileNumber?.mobile ?? loginCreds.toLowerCase().trim(),
				countryCode: validMobileNumber?.countryCode ?? country,
			};
			setMaskPhoneCreds(loginCreds);
			const response = await loginWithEmail(payload);
			if (response) {
				const { isVerifiedPhone, message, errorCode, errorData } = response;
				if (errorCode === 400) {
					if (errorData.message === Messages.PhoneError) {
						setSignInErrorType({
							...signInErrorType,
							type: 'mobile',
						});
						errorNotification(`${Messages.PhoneErrorMessage}`);
					} else if (errorData.message === Messages.AccountLocked) {
						errorNotification(`${Messages.AccountLockedErrorMessage}`);
					} else {
						setNewGmail('');
						navigate('/signup');
					}
					return;
				}
				if (errorCode) {
					onFailed({
						...(errorData.message ?? MESSAGE.ERROR),
						type: 'email',
					});
					setIsLargeErrorMsg(errorData.message);
					return;
				}
				if (isVerifiedPhone) {
					goToChooser();
					return;
				}
				if (isVerifiedPhone === false) {
					setIsVerifyModalOpen(true);
					errorNotification(Messages.NotAuth);
					const payload = {
						phone: loginCreds.toLowerCase().trim(),
						countryCode: country,
						sendSMS: true,
					};
					await userVerfication(payload);
				}
				setInvalidPhoneMessage(message);
				return;
			}
			setInvalidPhoneMessage(Messages.InvalidPhone);
			return;
		}
		setInvalidPhoneMessage(Messages.InvalidPhone);
		return;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		goToChooser,
		country,
		inputType,
		loginCreds,
		onFailed,
		onSuccess,
		signInErrorType,
		isVerifyModalOpen,
		userVerfication,
	]);

	const handleVerifyUsers = useCallback(async () => {
		const payload = {
			type: 'emailCode',
			email: loginCreds.toLowerCase().trim(),
			code: emailCode.trim(),
		};
		const response = await verifyEmailCode(payload);
		if (response) {
			const { token, message } = response;
			if (response.errorData?.message) {
				errorNotification(response.errorData.message);
			}
			onSuccess({ ...response, type: 'emailCode' });
			if (!token) {
				setInvalidEmailCodeMessage(message);
			}
		} else {
			onFailed({ ...(response ?? MESSAGE.ERROR), type: 'emailCode' });
			setInvalidEmailCodeMessage(MESSAGE.ERROR_TRY);
		}
	}, [
		emailCode,
		errorNotification,
		loginCreds,
		onFailed,
		onSuccess,
		verifyEmailCode,
	]);

	const handleOnClick = useCallback(
		(e: any) => {
			e.preventDefault();
			deleteCookie('user');
			deleteCookie('userEmail');
			localStorage.clear();
			if (step === 'CREDS') {
				handleSubmitCreds();

				return;
			}
			if (step === 'VERIFY') {
				handleVerifyUsers();
				return;
			}
		},
		[deleteCookie, step, handleSubmitCreds, handleVerifyUsers]
	);

	const isDisabled = useMemo(() => {
		if (step === 'VERIFY') {
			return !emailCode?.trim();
		}
		if (step === 'CREDS') {
			return !loginCreds?.trim() || !!invalidPhoneMessage;
		}
		return true;
	}, [emailCode, invalidPhoneMessage, loginCreds, step]);

	const handleOnChange = useCallback(
		(
			e: React.ChangeEvent<HTMLInputElement> | { target: { value: string } }
		) => {
			const { value } = e.target;
			const digits = value.replace(/\D/g, '');

			// A pasted / typed international number (starts with "+"): peel off the
			// dial code, select that country, and keep only the national digits.
			// Interpreting a leading "+1" as US is expected — the user can still
			// override the country afterwards via the dropdown, and the national
			// number already typed is left untouched.
			if (value?.trim()?.startsWith('+') && /^[\d ()+-]+$/.test(value)) {
				const code = fetchCountryCodeFromPhone(value);
				if (code) {
					setCountry(code);
					const national = value
						.slice(value.indexOf(code) + code.length)
						.replace(/\D/g, '');
					setLoginCreds(national.slice(0, MAX_PHONE_DIGITS));
				} else {
					setLoginCreds(digits.slice(0, MAX_PHONE_DIGITS));
				}
			} else if (inputType === 'phone') {
				// Plain digits only, capped at the E.164 ceiling (15). No fixed
				// 10/11 limit — per-country validity is checked on blur / submit.
				setLoginCreds(digits.slice(0, MAX_PHONE_DIGITS));
			} else {
				setLoginCreds(value);
			}

			setIsLargeErrorMsg('');
			setInvalidPhoneMessage('');
			setEmailCode('');
			setInvalidEmailCodeMessage('');
			setEmailBlurError('');
			setStep('CREDS');
			// Arun kumar : Giving gap to next btn wrapper
			if (nextDivRef.current) {
				nextDivRef.current.style.marginTop = '0px';
			}
		},
		[inputType, setCountry, setLoginCreds, setStep]
	);

	// Validate the entered number against the selected country's metadata when
	// the field loses focus. The length limits (lower AND upper) come from
	// libphonenumber-js per country — nothing is hardcoded to 10 digits.
	const handlePhoneBlur = useCallback(() => {
		if (inputType !== 'phone' || !loginCreds.trim()) {
			return;
		}
		const isTestPhoneNumber = TEST_PHONE_NUMBERS.includes(
			loginCreds.toLowerCase().trim()
		);
		if (isTestPhoneNumber) {
			return;
		}
		const countryObj = countries.find(item => item.label === country);
		const { isValid, message } = validatePhoneNumber(
			loginCreds,
			country,
			countryObj?.value
		);
		if (!isValid) {
			setInvalidPhoneMessage(message);
		}
	}, [inputType, loginCreds, country]);

	useEffect(() => {
		// Clear the invalid phone message when the country code changes
		setInvalidPhoneMessage('');
	}, [country]);

	const renderInputs = useMemo(() => {
		switch (step) {
			// Back in the flow as of 2026-09-16 — this is the screen that offers
			// Device Biometric (the passkey path) alongside the approval link.
			case 'VERIFY':
				return (
					<ChooseMethod
						methods={methods}
						{...props}
						locationInfo={locationInfo}
						ipAddress={ipAddress}
						loginPhoneNumber={loginCreds}
						send_otp_api={send_otp_api}
					/>
				);
			case 'CREDS':
				return (
					<>
						{inputType === 'phone' ? (
							<>
								<Input
									handleChange={handleOnChange}
									handleBlur={handlePhoneBlur}
									inputType="text" //keep type text as number is not giving suggestions for auto fill will use regex to accept number
									value={loginCreds}
									placeholder={``}
									label="Mobile Number"
									handleChangeCountry={e => {
										setCountry(e.label);
									}}
									isCountryCodeVisible={true}
									countryCode={country}
									isError={!!invalidPhoneMessage}
									errorMessage={invalidPhoneMessage}
									autoComplete="tel"
									isRequired
								/>

								{signInErrorType && renderVerificationSendLink()}
							</>
						) : (
							<>
								<Input
									inputType="text"
									handleChange={handleOnChange}
									value={loginCreds}
									placeholder=""
									label={inputLabel}
									isError={!!isLargeErrorMsg || !!emailBlurError}
									errorMessage={
										emailBlurError || (isMobileView ? Messages.isSmallErrorMsg : isLargeErrorMsg)
									}
									isRequired={true}
									autoFocus={true}
									handleBlur={handleEmailBlur}
									className="email-input-smooth"
								/>
								{signInErrorType && renderVerificationSendLink()}
							</>
						)}
					</>
				);
			default:
				return <></>;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		step,
		inputType,
		acountLiveStatus,
		loginCreds,
		emailCode,
		invalidEmailCodeMessage,
		methods,
		props,
		locationInfo,
		ipAddress,
		send_otp_api,
		country,
		invalidPhoneMessage,
		signInErrorType,
		isLargeErrorMsg,
		handlePhoneBlur,
		emailBlurError,
	]);

	const goToSingUp = useCallback(() => {
		navigate('/signup');
	}, [navigate]);

	const isDisableButton = useMemo(
		() => !isLoaded || isSocialDisable || isDisabled || !!isLargeErrorMsg || !!emailBlurError,
		[isLoaded, isDisabled, isSocialDisable, isLargeErrorMsg, emailBlurError]
	);

	const manageButtonLabel = useMemo((): string | JSX.Element => {
		if (!isLoaded) {
			return <Loader type="loader" className="loader-white" dimension={18} />;
		}
		return 'Next';
	}, [isLoaded]);

	const renderForm = useMemo(() => {
		return (
			<form className={style.form_wrapper} autoComplete="on">
				<div className={`${style.input_wrapper} input-wrapper`}>
					{renderInputs}
				</div>
				{step !== 'VERIFY' && (
					<div ref={nextDivRef} className="input-wrapper__next-wrapper">
						<Button
							type={'button__filled--primary button__large button__block'}
							handleClick={handleOnClick}
							label={manageButtonLabel}
							disabled={isDisableButton}
						/>
					</div>
				)}
			</form>
		);
	}, [renderInputs, step, handleOnClick, manageButtonLabel, isDisableButton]);

	/* SET THE CREDENTIALS, THEN SUBMIT ON A LATER RENDER — never in the same
	 * tick.
	 *
	 * This effect runs once, on mount, and used to call handleSubmitCreds()
	 * immediately after setLoginCreds(mobile). handleSubmitCreds is a
	 * useCallback closing over `loginCreds` and `country`, and React state
	 * updates do not apply until the next render — so the call read an EMPTY
	 * `loginCreds`, failed its own validation, and returned having done
	 * nothing. The field still filled in (that setState did land, one render
	 * later), so the screen showed the phone number sitting there with no
	 * progress: exactly what "Google SSO fills in the phone and stops" is.
	 *
	 * It was invisible until 2026-09-15 because the line above it,
	 * `setStep('VERIFY')`, advanced the user regardless of whether the submit
	 * did anything. Removing that line did not cause the no-op; it exposed it.
	 *
	 * This is the whole SSO handoff: the provider redirects to
	 * /login?phone=…&countryCode=…, and this is the code that acts on it.
	 * Gating on both values having actually landed means the submit runs with
	 * a fresh closure, and runs exactly once. */
	const [autoSubmitPending, setAutoSubmitPending] = useState(false);

	useEffect(() => {
		if (error) {
			errorNotification(decodeURIComponent(error));
		}
		if (mobile && countryCode) {
			setCountry(`+${countryCode.trim()}`);
			setLoginCreds(mobile);
			setMaskPhoneCreds(mobile);
			setAutoSubmitPending(true);
		}
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (!autoSubmitPending || !mobile || !countryCode) return;
		// Both pieces handleSubmitCreds reads must be the prefilled ones before
		// it is worth calling: it validates the number against the country.
		if (loginCreds !== mobile) return;
		if (country !== `+${countryCode.trim()}`) return;
		setAutoSubmitPending(false);
		handleSubmitCreds();
		// eslint-disable-next-line
	}, [autoSubmitPending, loginCreds, country, mobile, countryCode]);

	const [isImageLoad, setImageLoad] = useState(false);

	const showSkeltonLoader = useMemo(() => {
		if (!isImageLoad) {
			return (
				<div className="social-icon">
					<SessionInfoChild height={45} width={100} borderRadius={8} />
				</div>
			);
		}
		return <></>;
	}, [isImageLoad]);

	const hideIconOnLoader = classNames('social-icon', {
		'Social--Icon__hide': !isImageLoad,
	});

	const renderVerifyModal = useMemo(() => {
		return (
			isVerifyModalOpen && (
				<VerifyModal
					visible={isVerifyModalOpen}
					handleModal={() => setIsVerifyModalOpen(false)}
					regEmail={loginCreds.toLowerCase().trim()}
				/>
			)
		);
	}, [isVerifyModalOpen, loginCreds]);

	return (
		<div className={style.wrapper}>
			<div className={style.inner_wrapper}>
				<div className={style.inner}>
					{!(step === 'VERIFY') && (
						<div>
							<div className={style.title}>Log in</div>
							<div className={style.sub_title}>
								Please log in to your account.
							</div>
							<div className="account-wrapper">
								<div className="social-login-wrapper">
									<div className="social-login-wrapper__icon">
										{/*Anuj : Commented for patch release because from backend facebook login is not fixed */}
										{/* <div onClick={() => loginWithSocial('facebook')}>
											{showSkeltonLoader}
											<Image
												className={hideIconOnLoader}
												fileName="facebook.svg"
												onLoad={() => setImageLoad(true)}
											/>
										</div> */}
										{!isSocialLogin.google ? (
											<button
												disabled={isSocialDisable}
												onClick={() => loginWithSocial('google', host)}
											>
												<SocialMark brand="google" />
											</button>
										) : (
											<div className="social-login-wrapper--icon-loader">
												<Loader dimension={20} type="loader" />
											</div>
										)}
										{!isIframe && (
											<>
												{/* {!isSocialLogin.apple ? (
													<button
														disabled={isSocialDisable}
														onClick={() => loginWithSocial('apple', host)}
													>
														{showSkeltonLoader}
														<Image
															className={hideIconOnLoader}
															fileName="apple.svg"
															onLoad={() => setImageLoad(true)}
														/>
													</button>
												) : (
													<div className="social-login-wrapper--icon-loader">
														<Loader dimension={20} type="loader" />
													</div>
												)} */}
												{!isSocialLogin.microsoft ? (
													<button
														disabled={isSocialDisable}
														onClick={() => loginWithSocial('microsoft', host)}
													>
														<SocialMark brand="microsoft" />
													</button>
												) : (
													<div className="social-login-wrapper--icon-loader">
														<Loader dimension={20} type="loader" />
													</div>
												)}
											</>
										)}
									</div>
								</div>
								<div className="login-divider">
									<span>or</span>
								</div>
							</div>
						</div>
					)}
					{renderForm}
					{!(step === 'VERIFY') && (
						<div className="create-account">
							<span>Don’t have an account?</span>
							<div className="go-to-signup" onClick={goToSingUp}>
								Sign Up
							</div>
						</div>
					)}
				</div>
			</div>
			{renderVerifyModal}
		</div>
	);
};
